/* ==========================================================================
   Eos Hour — shop & product interactions (Phase 3)
   Vanilla JS, no libraries. Owns: shop filtering, gallery + lightbox,
   variant/stock UI, quantity stepper, one-tap bundle, sticky buy bar,
   AJAX adds with toast feedback, and the cart-drawer empty-state copy.
   Dawn's cart drawer behaviour is untouched (JS is Dawn's; this file
   only posts adds and swaps two text nodes in the empty state).
   ========================================================================== */
(function () {
  'use strict';

  var qs = function (sel, root) {
    return (root || document).querySelector(sel);
  };
  var qsa = function (sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  };

  function money(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  }

  /* ---------------- Toast ---------------- */
  var toastEl = null;
  var toastTimer = null;
  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'eos-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 2400);
  }

  function refreshCartCount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        qsa('[data-eos-cart-count]').forEach(function (badge) {
          badge.textContent = cart.item_count;
          badge.hidden = cart.item_count === 0;
        });
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          bubble.setAttribute(
            'aria-label',
            'Bag, ' + cart.item_count + ' item' + (cart.item_count === 1 ? '' : 's')
          );
        }
        return cart;
      })
      .catch(function () {
        return null;
      });
  }

  function addItems(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    });
  }

  /* Refresh the Dawn cart drawer from the server copy, then open it.
     The drawer markup is snippet-rendered in the layout, so we re-render
     the cart-drawer section and swap #CartDrawer in place (mirrors what
     Dawn's own renderContents does after an AJAX add). */
  function openCartDrawer() {
    var drawer = qs('cart-drawer');
    if (!drawer) return Promise.resolve();
    function doOpen() {
      if (typeof drawer.open === 'function') {
        try {
          drawer.open();
        } catch (e) {
          /* badge already updated by refreshCartCount */
        }
      }
    }
    return fetch('/?section_id=cart-drawer', { headers: { Accept: 'text/html' } })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshWrap = doc.querySelector('#CartDrawer');
        var currentWrap = qs('#CartDrawer');
        var freshEl = doc.querySelector('cart-drawer');
        if (freshWrap && currentWrap) {
          currentWrap.innerHTML = freshWrap.innerHTML;
          if (freshEl) drawer.className = freshEl.className;
          var overlay = qs('#CartDrawer-Overlay');
          if (overlay && typeof drawer.close === 'function') {
            overlay.addEventListener('click', function () {
              drawer.close();
            });
          }
        }
        doOpen();
      })
      .catch(function () {
        doOpen();
      });
  }

  /* Keep the header bag count + label in sync when Dawn's own cart
     flows (drawer steppers, cart page) change the cart without going
     through the Eos AJAX add path above. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      refreshCartCount();
    });
  }

  /* ---------------- AJAX add forms (quick-add, upsells) ------------ */
  /* Delegated on document: drawer items (and their upsell forms) are
     re-rendered by openCartDrawer, so per-form bindings would be lost
     after the first drawer refresh. Two nets: a delegated click on the
     submit button (fires first, cancels the native submit outright) and
     a delegated submit handler (Enter-key submissions). Both run the
     same in-place add so the drawer never navigates away to /cart. */
  function submitAddForm(form) {
    var idInput = form.querySelector('input[name="id"]');
    var qtyInput = form.querySelector('input[name="quantity"]');
    if (!idInput) return;
    var items = [
      {
        id: parseInt(idInput.value, 10),
        quantity: qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1,
      },
    ];
    addItems(items)
      .then(function () {
        var title = form.getAttribute('data-product-title') || 'Item';
        var variant = form.getAttribute('data-variant-title') || '';
        toast('Added: ' + title + (variant ? ' — ' + variant : ''));
        return refreshCartCount();
      })
      .then(function () {
        return openCartDrawer();
      })
      .catch(function () {
        toast('Something went wrong adding that. Please try again.');
      });
  }
  document.addEventListener('click', function (event) {
    var btn =
      event.target && event.target.closest
        ? event.target.closest(
            'form[data-eos-add-form] button[type="submit"], form[data-eos-add-form] input[type="submit"]'
          )
        : null;
    if (!btn) return;
    event.preventDefault();
    var form = btn.closest ? btn.closest('form[data-eos-add-form]') : null;
    if (form) submitAddForm(form);
  });
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[data-eos-add-form]')) return;
    event.preventDefault();
    submitAddForm(form);
  });

  /* ---------------- Back link (return to where you came from) ------ */
  var backLink = qs('[data-eos-back]');
  if (backLink && document.referrer) {
    try {
      var ref = new URL(document.referrer);
      if (ref.origin === window.location.origin && ref.href !== window.location.href) {
        backLink.addEventListener('click', function (event) {
          event.preventDefault();
          window.history.back();
        });
      }
    } catch (e) {
      /* no-op: keep the plain link */
    }
  }

  /* ---------------- Shop filtering --------------------------------- */
  var shopGrid = qs('[data-eos-shop-grid]');
  if (shopGrid) {
    var cards = qsa('[data-eos-card]', shopGrid);
    var countEl = qs('[data-eos-result-count]');
    var emptyEl = qs('[data-eos-shop-empty]');
    var totalEl = qs('script[data-eos-shop-total]');
    var total = cards.length;
    if (totalEl) {
      try {
        total = JSON.parse(totalEl.textContent) || cards.length;
      } catch (e) {
        total = cards.length;
      }
    }

    var applyFilter = function (filter) {
      var visible = 0;
      cards.forEach(function (card) {
        var show = filter === 'all' || card.getAttribute('data-category') === filter;
        card.hidden = !show;
        if (show) visible += 1;
      });
      shopGrid.setAttribute('data-visible', String(visible));
      if (emptyEl) emptyEl.hidden = visible !== 0;
      if (countEl) {
        countEl.textContent = visible + (visible === 1 ? ' piece' : ' pieces');
      }
      qsa('[data-eos-filter]').forEach(function (btn) {
        var active = btn.getAttribute('data-eos-filter') === filter;
        btn.classList.toggle('is-active', active);
        if (btn.hasAttribute('aria-pressed')) {
          btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        }
      });
    };

    qsa('[data-eos-filter]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var filter = btn.getAttribute('data-eos-filter');
        applyFilter(filter);
        if (btn.classList.contains('eos-cat-card')) {
          shopGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', filter === 'all' ? window.location.pathname : '#' + filter);
        }
      });
    });

    var initial = 'all';
    var preset = shopGrid.getAttribute('data-eos-initial-filter');
    if (preset) {
      initial = preset;
    }
    if (window.location.hash) {
      var hash = window.location.hash.slice(1);
      if (
        hash === 'the-travel-edit' ||
        hash === 'luxury' ||
        hash === 'gift-boxes' ||
        hash === 'care-products'
      ) {
        initial = hash;
      }
    }
    applyFilter(initial);
  }

  /* ---------------- Product page ----------------------------------- */
  var productRoot = qs('[data-eos-product]');
  var variantDataEl = qs('script[data-eos-variant-data]');
  if (!productRoot || !variantDataEl) return;

  var variants = [];
  try {
    variants = JSON.parse(variantDataEl.textContent) || [];
  } catch (e) {
    variants = [];
  }
  var productTitleEl = qs('.eos-product-title', productRoot);

  function variantById(id) {
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(id)) return variants[i];
    }
    return variants[0] || null;
  }

  /* Gallery */
  var galleryEl = qs('[data-eos-gallery]', productRoot);
  var stageImg = qs('[data-eos-stage-img]', productRoot);
  var counterEl = qs('[data-eos-counter]', productRoot);
  var thumbs = qsa('[data-eos-thumb]', productRoot);
  var stage = qs('[data-eos-stage]', productRoot);
  var singleImage = galleryEl && galleryEl.classList.contains('single');
  var current = 0;
  var suppressStageClick = false;

  // Start on the thumb the server marked active (selected variant image).
  thumbs.forEach(function (thumb, i) {
    if (thumb.classList.contains('is-active')) current = i;
  });

  function galleryCount() {
    return thumbs.length || (stageImg ? 1 : 0);
  }

  function setImage(index, focusThumb) {
    var count = galleryCount();
    if (!count || !stageImg) return;
    current = ((index % count) + count) % count;
    var thumb = thumbs[current];
    if (thumb) {
      stageImg.src = thumb.getAttribute('data-full');
    }
    thumbs.forEach(function (t, i) {
      t.classList.toggle('is-active', i === current);
      t.setAttribute('aria-pressed', i === current ? 'true' : 'false');
    });
    if (counterEl) counterEl.textContent = current + 1 + ' / ' + count;
    if (focusThumb && thumb) thumb.focus();
    syncLightbox();
  }

  var prevBtn = qs('[data-eos-prev]', productRoot);
  var nextBtn = qs('[data-eos-next]', productRoot);
  if (prevBtn) prevBtn.addEventListener('click', function () { setImage(current - 1); });
  if (nextBtn) nextBtn.addEventListener('click', function () { setImage(current + 1); });
  thumbs.forEach(function (thumb, i) {
    thumb.addEventListener('click', function () {
      setImage(i);
    });
  });

  /* Touch swipe: horizontal changes photo, vertical scroll untouched */
  if (stage) {
    var touchX = null;
    var touchY = null;
    stage.addEventListener(
      'touchstart',
      function (e) {
        if (e.touches.length === 1) {
          touchX = e.touches[0].clientX;
          touchY = e.touches[0].clientY;
        }
      },
      { passive: true }
    );
    stage.addEventListener(
      'touchend',
      function (e) {
        if (touchX === null || singleImage) return;
        var dx = e.changedTouches[0].clientX - touchX;
        var dy = e.changedTouches[0].clientY - touchY;
        if (Math.abs(dx) > 42 && Math.abs(dx) > Math.abs(dy) * 1.3) {
          suppressStageClick = true;
          setImage(dx < 0 ? current + 1 : current - 1);
          window.setTimeout(function () {
            suppressStageClick = false;
          }, 350);
        }
        touchX = null;
        touchY = null;
      },
      { passive: true }
    );
  }

  /* Lightbox */
  var lightbox = qs('[data-eos-lightbox]', productRoot);
  var lightboxImg = qs('[data-eos-lightbox-img]', productRoot);
  var lightboxCounter = qs('[data-eos-lightbox-counter]', productRoot);
  var lastFocus = null;

  function syncLightbox() {
    if (!lightbox || lightbox.hidden || !stageImg) return;
    if (lightboxImg) {
      lightboxImg.src = stageImg.src;
      var title = productTitleEl ? productTitleEl.textContent.trim() : '';
      lightboxImg.alt = title + ', alternate view, enlarged';
    }
    if (lightboxCounter && !singleImage) {
      lightboxCounter.textContent = current + 1 + ' / ' + galleryCount();
    }
  }

  function openLightbox() {
    if (!lightbox || !stageImg) return;
    lastFocus = document.activeElement;
    lightbox.hidden = false;
    syncLightbox();
    document.body.style.overflow = 'hidden';
    var closeBtn = qs('[data-eos-lightbox-close]', lightbox);
    if (closeBtn) closeBtn.focus();
  }

  function closeLightbox() {
    if (!lightbox) return;
    lightbox.hidden = true;
    document.body.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function lightboxOpen() {
    return lightbox && !lightbox.hidden;
  }

  if (stage) {
    stage.addEventListener('click', function (e) {
      if (suppressStageClick) return;
      if (e.target.closest('[data-eos-prev],[data-eos-next]')) return;
      openLightbox();
    });
  }
  var expandBtn = qs('[data-eos-expand]', productRoot);
  if (expandBtn) {
    expandBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      openLightbox();
    });
  }
  if (lightbox) {
    qs('[data-eos-lightbox-close]', lightbox).addEventListener('click', closeLightbox);
    var lbPrev = qs('[data-eos-lightbox-prev]', lightbox);
    var lbNext = qs('[data-eos-lightbox-next]', lightbox);
    if (lbPrev) lbPrev.addEventListener('click', function () { setImage(current - 1); });
    if (lbNext) lbNext.addEventListener('click', function () { setImage(current + 1); });
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) closeLightbox();
    });
    /* Keep Tab inside the lightbox while it is open */
    lightbox.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var focusables = qsa('button, [href]', lightbox).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  /* Keyboard gallery navigation (product page context only) */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      if (lightboxOpen()) closeLightbox();
      return;
    }
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    var target = e.target;
    if (
      target &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'SELECT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable)
    ) {
      return;
    }
    var drawer = qs('cart-drawer');
    if (drawer && drawer.classList.contains('active')) return;
    if (!productRoot.getBoundingClientRect) return;
    var rect = productRoot.getBoundingClientRect();
    var inView = rect.top < window.innerHeight && rect.bottom > 0;
    if (!inView) return;
    e.preventDefault();
    setImage(e.key === 'ArrowRight' ? current + 1 : current - 1);
  });

  /* Variant state */
  var variantSelect = qs('[data-eos-variant-select]', productRoot);
  var variantIdInput = qs('[data-eos-variant-id]', productRoot);
  var priceEl = qs('[data-eos-price]', productRoot);
  var selectedEl = qs('[data-eos-selected]', productRoot);
  var stockEl = qs('[data-eos-stock]', productRoot);
  var qtyWrap = qs('[data-eos-qty]', productRoot);
  var qtyInput = qs('[data-eos-qty-input]', productRoot);
  var addBtn = qs('[data-eos-add-btn]', productRoot);
  var notifyPanel = qs('[data-eos-notify]', productRoot);
  var notifyTags = qs('[data-eos-notify-tags]', productRoot);
  var stickyBar = qs('[data-eos-sticky-buy]', productRoot);
  var stickyTitle = qs('[data-eos-sticky-title]', productRoot);
  var stickyPrice = qs('[data-eos-sticky-price]', productRoot);
  var stickyBtn = qs('[data-eos-sticky-btn]', productRoot);
  var bundleMainVariantEl = qs('[data-eos-bundle-main-variant]', productRoot);
  var bundleMainPriceEl = qs('[data-eos-bundle-main-price]', productRoot);
  var currentVariant = null;

  var FLAME =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M13.4 2.2c.5 3.5-2.2 4.7-1.7 7.5.2 1.2 1.2 2 2.3 2.2-.2-1.4.5-2.6 1.7-3.5 2.4 2.1 3.8 4.5 3.8 7.1A7.5 7.5 0 1 1 7.7 9.4c-.1 2.4 1 3.7 2.1 4.1-.6-4.3.3-8.1 3.6-11.3Z"/></svg>';

  function renderVariant(variant) {
    if (!variant) return;
    currentVariant = variant;
    if (variantIdInput) variantIdInput.value = variant.id;
    if (priceEl) priceEl.textContent = money(variant.price);
    if (selectedEl) selectedEl.textContent = 'Selected: ' + variant.title;

    if (stockEl) {
      if (!variant.available) {
        stockEl.innerHTML = '<span class="eos-stock-line eos-sold-out">Sold out</span>';
      } else if (variant.quantity !== null && variant.quantity > 0 && variant.quantity <= 10) {
        stockEl.innerHTML =
          '<span class="eos-stock-line eos-stock-line--low">' +
          FLAME +
          'Selling fast · Only ' +
          variant.quantity +
          ' left</span>';
      } else {
        stockEl.innerHTML = '';
      }
    }

    var soldOut = !variant.available;
    if (qtyWrap) qtyWrap.style.display = soldOut ? 'none' : '';
    if (addBtn) {
      addBtn.textContent = soldOut ? 'Notify me when back in stock' : 'Add to bag';
      addBtn.classList.toggle('is-notify', soldOut);
    }
    if (notifyPanel) notifyPanel.hidden = !soldOut;
    if (notifyTags) {
      notifyTags.value = 'restock_' + variant.id;
    }

    if (stickyPrice) stickyPrice.textContent = money(variant.price);
    if (stickyTitle && productTitleEl) {
      var title = productTitleEl.textContent.trim();
      stickyTitle.textContent =
        variants.length > 1 ? title + ' — ' + variant.title : title;
    }
    if (stickyBtn) {
      stickyBtn.textContent = soldOut ? 'Notify me when back in stock' : 'Add to bag';
      stickyBtn.classList.toggle('is-notify', soldOut);
    }

    if (bundleMainVariantEl) bundleMainVariantEl.textContent = variant.title;
    if (bundleMainPriceEl) bundleMainPriceEl.textContent = money(variant.price);
    updateBundle();

    if (typeof variant.imageIndex === 'number' && variant.imageIndex >= 0) {
      setImage(variant.imageIndex);
    }
  }

  if (variantSelect) {
    variantSelect.addEventListener('change', function () {
      renderVariant(variantById(variantSelect.value));
    });
    renderVariant(variantById(variantSelect.value));
  } else if (variants.length) {
    renderVariant(variants[0]);
  }

  /* Quantity stepper */
  var qtyMinus = qs('[data-eos-qty-minus]', productRoot);
  var qtyPlus = qs('[data-eos-qty-plus]', productRoot);
  if (qtyMinus && qtyInput) {
    qtyMinus.addEventListener('click', function () {
      qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
    });
  }
  if (qtyPlus && qtyInput) {
    qtyPlus.addEventListener('click', function () {
      qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1;
    });
  }

  /* Primary buy behaviour: AJAX add with toast, or notify scroll */
  var productForm = qs('[data-eos-product-form]', productRoot);
  function scrollToNotify() {
    if (!notifyPanel) return;
    notifyPanel.hidden = false;
    notifyPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
    var email = notifyPanel.querySelector('input[type="email"]');
    if (email) email.focus({ preventScroll: true });
  }

  if (addBtn) {
    addBtn.addEventListener('click', function (e) {
      if (currentVariant && !currentVariant.available) {
        e.preventDefault();
        scrollToNotify();
      }
    });
  }
  if (productForm) {
    productForm.addEventListener('submit', function (e) {
      if (currentVariant && !currentVariant.available) {
        e.preventDefault();
        scrollToNotify();
        return;
      }
      e.preventDefault();
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      addItems([{ id: currentVariant.id, quantity: qty }])
        .then(function () {
          toast(
            'Added: ' +
              (productTitleEl ? productTitleEl.textContent.trim() : 'Item') +
              (variants.length > 1 ? ' — ' + currentVariant.title : '')
          );
          return refreshCartCount();
        })
        .then(function () {
          return openCartDrawer();
        })
        .catch(function () {
          productForm.submit();
        });
    });
  }

  /* Sticky buy bar */
  var buyRow = qs('[data-eos-buy-row]', productRoot);
  if (stickyBar && buyRow && 'IntersectionObserver' in window) {
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          stickyBar.hidden = entry.isIntersecting;
        });
      },
      { rootMargin: '-80px 0px 0px 0px' }
    );
    observer.observe(buyRow);
  }
  if (stickyBtn) {
    stickyBtn.addEventListener('click', function () {
      if (currentVariant && !currentVariant.available) {
        scrollToNotify();
        return;
      }
      if (addBtn) addBtn.click();
    });
  }

  /* One-tap bundle */
  var bundleEl = qs('[data-eos-bundle]', productRoot);
  var bundleDataEl = qs('script[data-eos-bundle-data]');
  function updateBundle() {
    if (!bundleEl || !bundleDataEl) return;
    var data;
    try {
      data = JSON.parse(bundleDataEl.textContent);
    } catch (e) {
      return;
    }
    var finishSelect = qs('[data-eos-bundle-finish]', bundleEl);
    var finishVariantSelect = qs('[data-eos-bundle-finish-variant]', bundleEl);
    var pillowSelect = qs('[data-eos-bundle-pillow]', bundleEl);
    var totalEl = qs('[data-eos-bundle-total]', bundleEl);
    var addBundleBtn = qs('[data-eos-bundle-add]', bundleEl);
    if (!finishSelect || !finishVariantSelect) return;

    var finishData = data.finishing[finishSelect.value];
    if (!finishData) return;

    var selectedFinishVariantId = finishVariantSelect.value;
    finishVariantSelect.innerHTML = '';
    finishData.variants.forEach(function (v) {
      var opt = document.createElement('option');
      opt.value = v.id;
      opt.textContent = v.title + (v.available ? '' : ' — Sold out');
      opt.disabled = !v.available;
      finishVariantSelect.appendChild(opt);
    });
    var stillThere = finishData.variants.some(function (v) {
      return String(v.id) === String(selectedFinishVariantId) && v.available;
    });
    if (!stillThere) {
      var firstAvail = finishData.variants.filter(function (v) {
        return v.available;
      })[0];
      if (firstAvail) finishVariantSelect.value = firstAvail.id;
    }
    var label = qs('[data-eos-bundle-finish-variant-label]', bundleEl);
    if (label) label.textContent = finishData.title + ' — choose color';

    var finishVariant = finishData.variants.filter(function (v) {
      return String(v.id) === String(finishVariantSelect.value);
    })[0];
    var pillowVariant = (data.pillow || []).filter(function (v) {
      return pillowSelect && String(v.id) === String(pillowSelect.value);
    })[0];

    var total =
      (currentVariant ? currentVariant.price : 0) +
      (pillowVariant ? pillowVariant.price : 0) +
      (finishVariant ? finishVariant.price : 0) +
      (data.puttyPrice || 0);
    if (totalEl) totalEl.textContent = money(total);

    var mainSoldOut = !currentVariant || !currentVariant.available;
    if (addBundleBtn) {
      addBundleBtn.disabled = mainSoldOut || !finishVariant || !pillowVariant;
      addBundleBtn.innerHTML = mainSoldOut
        ? 'Selected option is sold out'
        : 'Add complete set · <span data-eos-bundle-total>' + money(total) + '</span>';
    }
  }

  if (bundleEl && bundleDataEl) {
    ['[data-eos-bundle-finish]', '[data-eos-bundle-pillow]', '[data-eos-bundle-finish-variant]'].forEach(
      function (sel) {
        var el = qs(sel, bundleEl);
        if (el) el.addEventListener('change', updateBundle);
      }
    );
    updateBundle();

    var addBundleBtn2 = qs('[data-eos-bundle-add]', bundleEl);
    if (addBundleBtn2) {
      addBundleBtn2.addEventListener('click', function () {
        var data;
        try {
          data = JSON.parse(bundleDataEl.textContent);
        } catch (e) {
          return;
        }
        var pillowSelect = qs('[data-eos-bundle-pillow]', bundleEl);
        var finishVariantSelect = qs('[data-eos-bundle-finish-variant]', bundleEl);
        if (!currentVariant || !pillowSelect || !finishVariantSelect) return;
        var items = [
          { id: currentVariant.id, quantity: 1 },
          { id: data.puttyVariantId, quantity: 1 },
          { id: parseInt(pillowSelect.value, 10), quantity: 1 },
          { id: parseInt(finishVariantSelect.value, 10), quantity: 1 },
        ];
        addItems(items)
          .then(function () {
            toast('Complete set added at the full combined price.');
            return refreshCartCount();
          })
          .then(function () {
            return openCartDrawer();
          })
          .catch(function () {
            toast('Something went wrong adding the set. Please try again.');
          });
      });
    }
  }

  /* Cart drawer empty-state copy (prototype §5) */
  function fixEmptyCartCopy() {
    var drawer = qs('cart-drawer');
    if (!drawer) return;
    var heading = drawer.querySelector(
      '.cart-drawer__empty-content h2, .cart__empty-text, .drawer__empty h2'
    );
    if (heading && heading.textContent.trim() !== 'Your bag is ready.') {
      heading.textContent = 'Your bag is ready.';
      var sub = document.createElement('p');
      sub.className = 'eos-cart-empty-sub';
      sub.textContent = 'Add a case, then finish it with a care or gifting detail.';
      heading.insertAdjacentElement('afterend', sub);
    }
  }
  var drawerEl = qs('cart-drawer');
  if (drawerEl && 'MutationObserver' in window) {
    var drawerObserver = new MutationObserver(function () {
      if (drawerEl.classList.contains('active')) fixEmptyCartCopy();
    });
    drawerObserver.observe(drawerEl, { attributes: true, attributeFilter: ['class'] });
    fixEmptyCartCopy();
  }
})();
